import React, { useEffect, useState } from 'react';
import './UserGrid.css';
import SocialUserCard from '../../../components/social/SocialUserCard';
import { useNavigate } from 'react-router-dom';
import { useChatDock } from '../../../contexts/ChatDockContext';
import supabase from '../../../supabaseClient';

const getInitials = (name = '') => {
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || '';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const UserGrid = ({
  users,
  connectionRequests,
  handleSendConnectionRequest,
  connectedUserIds = [],
  currentUser,
  onlineUserIds = [],
  favoriteUserIds = [],
  handleToggleFavorite,
  mutualInterestMap = {},
  verifiedUserIds = [],
  handleRemoveConnection,
  onInviteToGroupEvent,
  onReportUser,
  onBlockUser,
  onSendGift,
}) => {
  const [quickActionsOpen, setQuickActionsOpen] = useState(null);
  // Hover preview state
  const [hoverUserId, setHoverUserId] = useState(null);
  const [mutualCounts, setMutualCounts] = useState({}); // userId -> count
  const [mutualLoading, setMutualLoading] = useState({});

  // Modal state for each action
  const [modal, setModal] = useState({ type: null, user: null });

  // Add handlers for new actions
  const handleInvite = (user) => setModal({ type: 'invite', user });
  const handleReport = (user) => setModal({ type: 'report', user });
  const handleBlock = (user) => setModal({ type: 'block', user });
  const handleSendGift = (user) => setModal({ type: 'gift', user });
  const handleRemoveConnectionModal = (user) => setModal({ type: 'remove', user });

  const handleCopyProfileLink = (user) => {
    const url = `${window.location.origin}/profile/${user.id}`;
    navigator.clipboard.writeText(url);
    alert('Profile link copied!');
  };

  // Modal close handler
  const closeModal = () => setModal({ type: null, user: null });

  const handleQuickActions = (userId, e) => {
    e.stopPropagation();
    setQuickActionsOpen(quickActionsOpen === userId ? null : userId);
  };

  const handleFavorite = (userId, e) => {
    e.stopPropagation();
    if (handleToggleFavorite) handleToggleFavorite(userId);
  };

  const navigate = useNavigate();
  const { openChat } = useChatDock();

  // Lazy-load mutual connections when hovering a card
  useEffect(() => {
    async function fetchMutual(uId) {
      if (!currentUser?.id || !uId || uId === currentUser.id) return;
      if (mutualCounts[uId] != null || mutualLoading[uId]) return;
      setMutualLoading(prev => ({ ...prev, [uId]: true }));
      try {
        // Current user's accepted connections (both directions)
        const [{ data: out1 }, { data: in1 }] = await Promise.all([
          supabase.from('connections').select('connection_id').eq('user_id', currentUser.id).eq('status', 'accepted'),
          supabase.from('connections').select('user_id').eq('connection_id', currentUser.id).eq('status', 'accepted')
        ]);
        const mySet = new Set([...(out1 || []).map(r => r.connection_id), ...(in1 || []).map(r => r.user_id)]);
        // Hovered user's accepted connections
        const [{ data: out2 }, { data: in2 }] = await Promise.all([
          supabase.from('connections').select('connection_id').eq('user_id', uId).eq('status', 'accepted'),
          supabase.from('connections').select('user_id').eq('connection_id', uId).eq('status', 'accepted')
        ]);
        const otherSet = new Set([...(out2 || []).map(r => r.connection_id), ...(in2 || []).map(r => r.user_id)]);
        // Intersection size
        let count = 0;
        for (const id of mySet) if (otherSet.has(id)) count++;
        setMutualCounts(prev => ({ ...prev, [uId]: count }));
      } catch (e) {
        setMutualCounts(prev => ({ ...prev, [uId]: 0 }));
      } finally {
        setMutualLoading(prev => ({ ...prev, [uId]: false }));
      }
    }
    if (hoverUserId) fetchMutual(hoverUserId);
  }, [hoverUserId, currentUser?.id]);

  return (
    <>
      <div className="user-grid">
        {users.map(user => {
          const isNew = user.created_at && (Date.now() - new Date(user.created_at).getTime() < 7 * 24 * 60 * 60 * 1000);
          const isConnected = connectedUserIds.includes(user.id);
          const canMessage = isConnected || user.allow_messages_from_anyone === true;
          const isSelf = currentUser && user.id === currentUser.id;
          const isPending = connectionRequests[user.id] === 'pending';
          const isOnline = onlineUserIds.includes(user.id);
          const isFavorited = favoriteUserIds.includes(user.id);
          const hasMutual = mutualInterestMap[user.id] && mutualInterestMap[user.id].length > 0;
          const isVerified = verifiedUserIds.includes(user.id);

          return (
            <div
              key={user.id}
              onMouseEnter={() => setHoverUserId(user.id)}
              onMouseLeave={() => setHoverUserId(prev => (prev === user.id ? null : prev))}
            >
              <SocialUserCard
                user={user}
                isSelf={isSelf}
                isConnected={isConnected}
                isPending={isPending}
                canMessage={canMessage}
                isOnline={isOnline}
                isNew={isNew}
                isFavorited={isFavorited}
                hasMutual={hasMutual}
                isVerified={isVerified}
                mutualLabel={mutualInterestMap[user.id] ? `Mutual interests: ${mutualInterestMap[user.id].join(', ')}` : null}
                onFavoriteToggle={() => handleFavorite(user.id)}
                onClick={() => window.open(`/app/profile/${user.id}`, '_blank', 'noopener,noreferrer')}
                onConnect={() => handleSendConnectionRequest(user.id)}
                onMessage={() => openChat(user.id)}
                showHoverDetails={hoverUserId === user.id}
              />
            </div>
          );
        })}
      </div>

      {/* Action Modals */}
      {modal.type === 'invite' && (
        <div className="event-modal-backdrop" onClick={closeModal}>
          <div className="event-modal" onClick={e => e.stopPropagation()}>
            <button className="close-modal-btn" onClick={closeModal}>×</button>
            <h2 className="event-modal-title">Invite to Group/Event</h2>
            <div className="event-modal-description">
              Invite <strong>{modal.user.name}</strong> to a group or event.
            </div>
            <form
              onSubmit={async e => {
                e.preventDefault();
                const group = e.target.group.value;
                if (onInviteToGroupEvent) {
                  await onInviteToGroupEvent(modal.user.id, group);
                }
                closeModal();
              }}
            >
              <div style={{ marginBottom: '1rem' }}>
                <label>
                  Group/Event:
                  <input
                    name="group"
                    type="text"
                    placeholder="Enter group or event name"
                    required
                    style={{ marginLeft: 8, padding: 4, borderRadius: 4, border: '1px solid #ccc' }}
                  />
                </label>
              </div>
              <div className="event-modal-actions">
                <button className="attend-btn" type="submit">Send Invite</button>
                <button className="leave-btn" type="button" onClick={closeModal}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {modal.type === 'report' && (
        <div className="event-modal-backdrop" onClick={closeModal}>
          <div className="event-modal" onClick={e => e.stopPropagation()}>
            <button className="close-modal-btn" onClick={closeModal}>×</button>
            <h2 className="event-modal-title">Report User</h2>
            <form
              onSubmit={async e => {
                e.preventDefault();
                const reason = e.target.reason.value;
                const details = e.target.details.value;
                if (onReportUser) {
                  await onReportUser(modal.user.id, reason, details);
                }
                closeModal();
              }}
            >
              <div className="event-modal-description">
                Report <strong>{modal.user.name}</strong> for inappropriate behavior.
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label>
                  Reason:
                  <select name="reason" required style={{ marginLeft: 8, padding: 4, borderRadius: 4 }}>
                    <option value="">Select reason</option>
                    <option value="spam">Spam</option>
                    <option value="abuse">Abusive behavior</option>
                    <option value="inappropriate">Inappropriate content</option>
                    <option value="other">Other</option>
                  </select>
                </label>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <textarea
                  name="details"
                  placeholder="Additional details (optional)"
                  rows={3}
                  style={{ width: '100%', borderRadius: 4, padding: 4, border: '1px solid #ccc' }}
                />
              </div>
              <div className="event-modal-actions">
                <button className="attend-btn" type="submit">Submit Report</button>
                <button className="leave-btn" type="button" onClick={closeModal}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {modal.type === 'block' && (
        <div className="event-modal-backdrop" onClick={closeModal}>
          <div className="event-modal" onClick={e => e.stopPropagation()}>
            <button className="close-modal-btn" onClick={closeModal}>×</button>
            <h2 className="event-modal-title">Block User</h2>
            <div className="event-modal-description">
              Are you sure you want to block <strong>{modal.user?.display_name || modal.user?.name || 'this user'}</strong>?<br />
              This will prevent them from contacting you or seeing your profile.
            </div>
            <div className="danger-zone-label">Danger zone</div>
            <div className="event-modal-actions">
              <button
                className="danger-action-btn"
                onClick={async () => {
                  const confirmed = window.confirm(
                    `Block ${modal.user?.display_name || modal.user?.name || 'this user'}? This action cannot be undone.`
                  );
                  if (!confirmed) return;
                  if (onBlockUser) {
                    await onBlockUser(modal.user.id);
                  }
                  closeModal();
                }}
              >
                Block User
              </button>
              <button className="leave-btn" onClick={closeModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {modal.type === 'gift' && (
        <div className="event-modal-backdrop" onClick={closeModal}>
          <div className="event-modal" onClick={e => e.stopPropagation()}>
            <button className="close-modal-btn" onClick={closeModal}>×</button>
            <h2 className="event-modal-title">Send Gift/Badge</h2>
            <form
              onSubmit={async e => {
                e.preventDefault();
                const gift = e.target.gift.value;
                if (onSendGift) {
                  await onSendGift(modal.user.id, gift);
                }
                closeModal();
              }}
            >
              <div className="event-modal-description">
                Send a gift or badge to <strong>{modal.user.name}</strong>.
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label>
                  Gift/Badge:
                  <select name="gift" required style={{ marginLeft: 8, padding: 4, borderRadius: 4 }}>
                    <option value="">Select gift/badge</option>
                    <option value="🌟 Star">🌟 Star</option>
                    <option value="🏅 Medal">🏅 Medal</option>
                    <option value="🎉 Celebration">🎉 Celebration</option>
                    <option value="👏 Applause">👏 Applause</option>
                  </select>
                </label>
              </div>
              <div className="event-modal-actions">
                <button className="attend-btn" type="submit">Send</button>
                <button className="leave-btn" type="button" onClick={closeModal}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {modal.type === 'remove' && (
        <div className="event-modal-backdrop" onClick={closeModal}>
          <div className="event-modal" onClick={e => e.stopPropagation()}>
            <button className="close-modal-btn" onClick={closeModal}>×</button>
            <h2 className="event-modal-title">Remove Connection</h2>
            <div className="event-modal-description">
              Are you sure you want to remove your connection with <strong>{modal.user.name}</strong>?
            </div>
            <div className="event-modal-actions">
              <button
                className="attend-btn"
                onClick={() => {
                  if (handleRemoveConnection) handleRemoveConnection(modal.user.id);
                  closeModal();
                }}
              >
                Remove
              </button>
              <button className="leave-btn" onClick={closeModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserGrid;